import {
  createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel
} from "@tanstack/react-table";
import { type Business } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import Table from "src/components/Table/Table";

import styles from "src/components/Business/ActiveChildrenTable.module.css";
import { ContextMenu, ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { ContextMenuIcon } from "src/components/Icon/Navigation";
import { useBusinessChildren } from "src/hooks/useBusinessChildren";
import { useParams } from "react-router-dom";
import { useMemo } from "react";
import { useTrackedTableSort } from "./useTrackedTableSort";

const { DeleteIcon } = action;

const activeChildrenTableColumnHelper = createColumnHelper<Business>();

const caption = "Active children accounts";

export const ActiveChildrenTable = () => {
  const { businessId: parentBusinessId } = useParams<{ businessId: string }>();
  const { businessChildren, loadingBusinessChildren, removeOneBusinessChild } = useBusinessChildren(parentBusinessId);

  const activeChildrenTableColumns = useMemo(() => [
    activeChildrenTableColumnHelper.accessor("name", {
      header: () => <span>Name</span>,
      cell: ({ getValue }) => (
        <div className={styles.activeChildName}>
          {getValue()}
        </div>
      )
    }),
    activeChildrenTableColumnHelper.accessor("department", {
      header: () => <span>Department</span>,
      cell: ({ getValue }) => (
        <div className={styles.activeChildDepartment}>
          {getValue() ?? "N/A"}
        </div>
      )
    }),
    activeChildrenTableColumnHelper.accessor("country", {
      header: () => <span>Country</span>,
      cell: ({ getValue }) => (
        <div className={styles.activeChildCountry}>
          {getValue() ?? "N/A"}
        </div>
      )
    }),
    activeChildrenTableColumnHelper.accessor("id", {
      header: () => <span />,
      enableSorting: false,
      cell: ({ getValue }) => (
        <div className={styles.menuContainer}>
          <ContextMenu toggleIcon={ContextMenuIcon} toggleButtonProps={{ title: "Toggle context menu for the child" }}>
            <ContextMenuItem label="Remove" icon={DeleteIcon} onClick={() => removeOneBusinessChild({ parentBusinessId, childBusinessId: getValue() })} />
          </ContextMenu>
        </div>
      )
    })
  ], [parentBusinessId, removeOneBusinessChild]);

  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const activeBusinessChildrenTable = useReactTable({
    data: businessChildren,
    columns: activeChildrenTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange
  });
  const activeBusinessChildrenRowModel = activeBusinessChildrenTable.getRowModel();
  const activeBusinessChildrenRows = useMemo(
    () => activeBusinessChildrenRowModel.rows, [activeBusinessChildrenRowModel]
  );

  return (
    <Table<Business>
      caption={caption}
      loading={loadingBusinessChildren}
      className={styles.table}
      rows={activeBusinessChildrenRows}
      columns={activeBusinessChildrenTable.getAllColumns()}
      headerGroups={activeBusinessChildrenTable.getHeaderGroups()}
      headerGroupProps={{
        cellClassName: styles.tableHeader
      }}
      rowProps={{
        className: styles.tableRow,
        cellClassName: styles.tableCell
      }}
      noDataText="No active children accounts"
    />
  );
};
